import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { CommentContext } from '@gitbook/app-database';
import type * as hiveCore from '@gitbook/hive-core';
import { type Document, serialize } from '@gitbook/slate';

import { useNavigation } from '../../navigation';
import { apiClient } from '../../state';
import { CommentContextLink } from './CommentContextLink';
import { CommentInput } from './CommentInput';
import { CommentTargetPreview } from './CommentTargetPreview';

/**
 * New comment box, shown at the bottom of the sidesheet. Handles new comments
 * at both the page-level and comments on specific nodes.
 */
export function PostCommentBox(props: {
    currentPage: hiveCore.RevisionPageDocument;
    context: CommentContext;
}): React.ReactElement {
    const { currentPage, context } = props;

    const { navigate } = useNavigation();
    const shouldReduceMotion = useReducedMotion();

    /** Create the comment and open it */
    const onSubmit = async (body: Document) => {
        const data: api.PostCommentSchema = {
            node: context.node || undefined,
            page: context.page,
            body: {
                document: serialize(body),
            },
            ...(context.tableCell ? { tableCell: context.tableCell } : {}),
        };

        const response = context.changeRequest
            ? await apiClient.changeRequestComments.postCommentInChangeRequest(
                  context.space,
                  context.changeRequest,
                  data
              )
            : await apiClient.spaceComments.postCommentInSpace(context.space, data);

        const comment = response.data;

        navigate({
            screen: {
                sideSheet: {
                    type: 'comments',
                    comment: comment.id,
                    node: comment.target.node ? comment.target.node.id : undefined,
                    context: undefined,
                    ...(context.tableCell ? { tableCell: context.tableCell } : {}),
                },
            },
        });
    };

    const onCancel = () => {
        navigate({
            current: {
                sideSheet: {
                    type: 'comments',
                    comment: undefined,
                    node: undefined,
                    context: undefined,
                    tableCell: undefined,
                },
            },
        });
    };

    const duration150ms = shouldReduceMotion ? 0 : 0.15;
    const duration100ms = shouldReduceMotion ? 0 : 0.1;

    const container = {
        showContext: {
            height: 'auto',
            transition: {
                duration: duration100ms,
                when: 'beforeChildren',
            },
        },
        hideContext: {
            height: 0,
            transition: {
                duration: duration100ms,
                when: 'afterChildren',
            },
        },
    };

    const contextLabel = {
        showContext: {
            opacity: 1,
            y: 0,
            transition: {
                duration: duration150ms,
            },
        },
        hideContext: {
            opacity: 0,
            y: 10,
            transition: {
                duration: duration100ms,
            },
        },
    };

    return (
        <div className="flex flex-col gap-3 border-base border-t px-4 pt-2 pb-2">
            <AnimatePresence mode="wait" initial={false}>
                <motion.div
                    key="container"
                    initial="hideContext"
                    animate="showContext"
                    exit="hideContext"
                    variants={container}
                    className="mt-0.5"
                >
                    <motion.div key="context-label" variants={contextLabel}>
                        <CommentContextLink
                            page={currentPage}
                            prefix={
                                context.node
                                    ? 'Commenting on block in '
                                    : 'Comment on the entire page '
                            }
                        />
                        {context.node ? (
                            <CommentTargetPreview
                                spaceId={context.space}
                                pageId={currentPage.id}
                                tableCell={context.tableCell}
                                nodeKey={context.node}
                            />
                        ) : null}
                    </motion.div>
                </motion.div>
            </AnimatePresence>
            <CommentInput
                targetNode={context.node || 'no_node'}
                testID="comment.postInput"
                placeholder="Leave a comment…"
                onSubmit={onSubmit}
                onCancel={onCancel}
                maxRows={12}
            />
        </div>
    );
}
